import Icons from './seatIcons/Icons';
import dom from '../utils/dom';
import { srOnlySpan, srOnlyElement } from '../utils/screen-reader/html';
import CharacterIcons from './seatIcons/CharacterIcons';
import SyosSeat from './SyosSeat';
import { LegendData, Zone } from './api/types';
import SyosConfig from './SyosConfig';
import { IIconRenderer } from './seatIcons/IconRenderer';
import { ISyosLabels } from './labels/types';

function makeIconTermWithDetails(iconSvgMarkup: string, htmlContent: string, color?: string) {
  const styleAttr = color ? `style="color: ${color};"` : '';

  return `
    <li class="tn-syos-legend__description-group">
      <div class="tn-syos-legend__description-term" ${styleAttr}>${iconSvgMarkup}</div>
      <div class="tn-syos-legend__description-details">
        <div class="tn-syos-legend__description-details-content">
          ${htmlContent}
        </div>
      </div>
    </li>
  `;
}

function makeZonePriceTypeListHtml(shouldRender: boolean, zone: Zone) {
  if (!shouldRender) {
    return '';
  }

  const priceTypeItemsHtml = zone.priceTypes.map((priceType) => `
    <li class="tn-syos-legend__zone-pricetype-list-item">
      <span class="tn-syos-legend__zone-pricetype-label">${priceType.price_type_desc}</span>
      <span class="tn-syos-legend__zone-pricetype-price">${priceType.displayPrice}</span>
    </li>`);

  const listLabel = `Price by ticket type for ${zone.label}`;

  return `
    <ul class="tn-syos-legend__zone-pricetype-list" aria-label="${listLabel}">
      ${priceTypeItemsHtml.join('')}
    </ul>`;
}

function makeZonePriceRangeHtml(shouldRender: boolean, zone: Zone, priceRangeSeparator: string) {
  if (!shouldRender) {
    return '';
  }

  const range = zone.formattedPrices;

  let value = `<span class="tn-syos-legend__zone-price-range-min-price">${range[0]}</span>`;

  if (range.length > 1) {
    value += ' ';
    value += `<span class="tn-syos-legend__zone-price-range-separator">${priceRangeSeparator}</span>`;
    value += ' ';
    value += `<span class="tn-syos-legend__zone-price-range-max-price">${range[range.length - 1]}</span>`;
  }

  return `
    <div class="tn-syos-legend__zone-price-range">
      ${srOnlySpan('Price: ')}
      ${value}
    </div>`;
}

class SyosLegendRenderer {
  private isPriceRangeDisplayed: boolean;

  private isPriceTypeListDisplayed: boolean;

  constructor(private iconRenderer: IIconRenderer, config: SyosConfig, private labels: ISyosLabels) {
    this.isPriceRangeDisplayed = config.legendShouldRenderPriceRange;
    this.isPriceTypeListDisplayed = config.legendShouldRenderPriceTypeList;
  }

  render(legendData: LegendData, id: string) {
    const $el = dom.makeElement('div', { class: 'tn-syos-legend', id });

    const zoneListHeadingId = 'tn-syos-legend-zone-list-heading';
    const seatTypesHeadingId = 'tn-syos-legend-seat-type-list-heading';

    const zoneListHtml = this.makeZoneListHtml(legendData.zones, zoneListHeadingId);
    const iconListHtml = this.makeSeatTypeListHtml(legendData.seatTypes, seatTypesHeadingId);

    $el.innerHTML = `
      ${srOnlyElement('h5', 'Sections', { id: zoneListHeadingId })}
      ${zoneListHtml}

      ${srOnlyElement('h5', 'Seat Type Icons', { id: seatTypesHeadingId })}
      ${iconListHtml}`;

    return $el;
  }

  makeZoneListHtml(zones: Zone[], listHeadingId: string) {
    const { iconRenderer } = this;

    const zoneListItemsHtml = zones.map((zone) => {
      const zoneLabelHtml = `<div class="tn-syos-legend__zone-label">${zone.label}</div>`;

      const zonePriceRangeHtml = makeZonePriceRangeHtml(this.isPriceRangeDisplayed, zone, this.labels.priceRangeSeparator);

      const priceTypeListHtml = makeZonePriceTypeListHtml(this.isPriceTypeListDisplayed, zone);

      const zoneHtmlContent = zoneLabelHtml + zonePriceRangeHtml + priceTypeListHtml;

      const icon = CharacterIcons.getCharacterIcon(zone.iconCharacter);

      const ariaLabel = `Character ${zone.iconCharacter} within a circle`;

      return makeIconTermWithDetails(iconRenderer.getSvgMarkup(icon, ariaLabel), zoneHtmlContent, zone.color);
    });

    return `
      <ul class="tn-syos-legend__zone-list" aria-labelledby="${listHeadingId}">
        ${zoneListItemsHtml.join('')}
      </ul>`;
  }

  makeSeatTypeListHtml(seatTypes: string[], listHeadingId: string) {
    const { iconRenderer, labels } = this;

    const icons = [
      {
        svgMarkup: iconRenderer.getSvgMarkup(Icons.Square, 'Solid gray square', SyosSeat.CSS_CLASS_UNAVAILABLE),
        description: labels.seatStatusUnavailable,
      },
    ];

    if (seatTypes.includes('NotForSale')) {
      icons.push({
        svgMarkup: iconRenderer.getSvgMarkup(Icons.EmptySet, 'Circle with an angled slash through it', SyosSeat.CSS_CLASS_UNAVAILABLE),
        description: labels.seatTypeNotForSale,
      });
    }

    icons.push(
      { svgMarkup: iconRenderer.getSvgMarkup(Icons.Donut, 'Circled dot'), description: labels.seatStatusSelected },
    );

    if (seatTypes.includes('Wheelchair')) {
      icons.push({ svgMarkup: iconRenderer.getSvgMarkup(Icons.Wheelchair, 'Wheelchair symbol within a circle'), description: labels.seatTypeWheelchair });
    }

    if (seatTypes.includes('Special')) {
      icons.push({ svgMarkup: iconRenderer.getSvgMarkup(Icons.Star, 'Star'), description: labels.seatTypeSpecial });
    }

    const listItemsHtml = icons
      .map((icon) => makeIconTermWithDetails(icon.svgMarkup, icon.description));

    return `
      <ul class="tn-syos-legend__seat-type-list" aria-labelledby="${listHeadingId}">
        ${listItemsHtml.join('')}
      </ul>`;
  }
}

export default SyosLegendRenderer;
